import Dexie, { type Table } from 'dexie';

import type {
  Comment,
  Edit,
  Message,
  Project,
  SavedFile,
  Setting,
  SettingId,
  TweakValue,
  Version,
} from '@/lib/schemas';
import { SettingSchema } from '@/lib/schemas';

export const DB_NAME = 'cdc';

export class CdcDb extends Dexie {
  projects!: Table<Project, string>;
  versions!: Table<Version, string>;
  messages!: Table<Message, string>;
  comments!: Table<Comment, string>;
  edits!: Table<Edit, string>;
  tweakValues!: Table<TweakValue, string>;
  settings!: Table<Setting, string>;
  savedFiles!: Table<SavedFile, string>;

  constructor(name = DB_NAME) {
    super(name);
    this.version(1).stores({
      projects: 'id, createdAt, updatedAt',
      versions: 'id, projectId, createdAt, [projectId+createdAt]',
      messages: 'id, projectId, versionId, createdAt, [projectId+createdAt]',
      comments: 'id, projectId, versionId, [versionId+selector]',
      edits: 'id, projectId, versionId, selector, [versionId+selector]',
      tweakValues: 'id, projectId, versionId, [versionId+variable]',
      settings: 'id',
    });
    this.version(2).stores({
      savedFiles: 'id, createdAt',
    });
  }
}

let _db: CdcDb | null = null;
export function db(): CdcDb {
  if (!_db) _db = new CdcDb();
  return _db;
}

/** Test-only: replace the singleton with a uniquely-named DB so vitest tests are isolated. */
export function _setDbForTesting(instance: CdcDb): void {
  _db = instance;
}

export async function getSetting<T extends SettingId>(
  id: T,
): Promise<Extract<Setting, { id: T }> | undefined> {
  const row = await db().settings.get(id);
  if (!row) return undefined;
  return SettingSchema.parse(row) as Extract<Setting, { id: T }>;
}

export async function putSetting(setting: Setting): Promise<void> {
  await db().settings.put(SettingSchema.parse(setting));
}

export async function deleteSetting(id: SettingId): Promise<void> {
  await db().settings.delete(id);
}
